import { MessageDescriptor } from "react-intl";

import api from "@api";
import { useToast } from "@UI/hooks/useToast";
import useI18n from "@I18n/hooks/useI18n";

import messages from "../messages";
import { clearAuthIdentity } from "./useAuthIdentity";
import { usePublishServiceRequest } from "./usePublishServiceRequest";

/**
 * The ending both auth branches share, once the visitor is authenticated: the
 * draft's claim publishes their request and the success modal takes over. The
 * last config screen ends here too, for a draft started before logging in.
 *
 * `toastId` is fixed per screen so a repeated failure replaces its toast
 * instead of stacking.
 */
export const useAuthStepCompletion = (toastId: string, screenKey: string) => {
  const { formatMessage } = useI18n();
  const toast = useToast();

  const { publish, isPublishing, publishedRequestId } =
    usePublishServiceRequest(screenKey);

  const showAccountError = (message: MessageDescriptor) => {
    if (toast.isActive(toastId)) return;

    toast({
      id: toastId,
      status: "error",
      description: formatMessage(message),
    });
  };

  const publishAndFinish = async () => {
    try {
      await publish();
      // Only after it succeeds: the identity keeps this screen mounted, so
      // clearing it first bounced a failed publish back, with no way to retry.
      clearAuthIdentity();
    } catch (error: unknown) {
      // Soft-banned visitors get sent to the dashboard's blocked modal
      // instead of a generic toast, matching the server-redirect pattern
      // TasksController#task_creation already applies for the same case.
      if (
        api.isApiError(error) &&
        error.response?.data?.error === "cannot_create_task"
      ) {
        window.location.href = "/home_listposter?cannot_create_task=true";
        return;
      }
      showAccountError(messages["taskCreation.error.saveFailed"]);
    }
  };

  return {
    showAccountError,
    publishAndFinish,
    isPublishing,
    publishedRequestId,
  };
};
